// Search RUM: what typing in a search box actually feels like, measured in the browser.
//
// Server timing sees none of the network, the tier fallthrough or the render, which
// between them are most of what the user experiences. So each box records, per session
// (first keystroke until the pick or the dismissal):
//
//  - keystroke -> first painted result, per query, with the query-length bucket and the
//    tier that painted it (`local` worker, omnisearch `server`, or the `legacy` endpoint);
//  - how many queries got a final answer, and which of them answered nothing (the backlog
//    for the measure vocabulary and the AI tier);
//  - the row the user picked, or that they dismissed the box without picking.
//
// One `search_session` event per sampled session goes to ORTEX's own
// `/api/tracking/event` (`trackSearchSession`), sent when the browser is idle. Nothing
// here runs on the keystroke beyond a `performance.now()` and a Map write, and nothing
// can throw into the search box.

import { trackSearchSession } from "@/lib/tracking";

import { SearchSource } from "./click";

export type SearchTier = "local" | "server" | "legacy";

/** Share of sessions reported. Sent as `sample_rate` so a report can scale counts back up. */
const SAMPLE_RATE = 0.25;
/** Caps per session, so one long session cannot send a large payload. */
const MAX_TIMINGS = 40;
const MAX_ZERO_QUERIES = 10;
const MAX_PENDING_TEXTS = 50;

interface Session {
    sampled: boolean;
    startPath: string;
    queries: number;
    answered: number;
    zeroCount: number;
    zero: Set<string>;
    /** [query length bucket, ms to first paint, tier] */
    timings: [string, number, SearchTier][];
    selectedRank: number | null;
}

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

function lengthBucket(text: string): string {
    const n = text.trim().length;
    if (n <= 3) return String(n);
    if (n <= 6) return "4-6";
    if (n <= 10) return "7-10";
    return "11+";
}

/** Runs `fn` once the frame that is being rendered now has been painted. */
function afterPaint(fn: () => void): void {
    if (typeof requestAnimationFrame !== "function") return fn();
    requestAnimationFrame(() => setTimeout(fn, 0));
}

function whenIdle(fn: () => void): void {
    const idle = typeof window !== "undefined" ? (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback : undefined;
    if (idle) idle(fn, { timeout: 5000 });
    else setTimeout(fn, 1000);
}

export class SearchRum {
    private session: Session | null = null;
    /** Keystroke time per text, until that text first paints. */
    private typedAt = new Map<string, number>();

    constructor(private readonly source: SearchSource) {}

    /** The box now holds `text`. Call from onChange. */
    keystroke(text: string): void {
        try {
            if (!text.trim()) return;
            if (!this.session) {
                this.session = {
                    sampled: Math.random() < SAMPLE_RATE,
                    startPath: typeof window !== "undefined" ? window.location.pathname : "",
                    queries: 0,
                    answered: 0,
                    zeroCount: 0,
                    zero: new Set(),
                    timings: [],
                    selectedRank: null,
                };
            }
            this.session.queries += 1;
            this.typedAt.delete(text);
            this.typedAt.set(text, now());
            if (this.typedAt.size > MAX_PENDING_TEXTS) this.typedAt.delete(this.typedAt.keys().next().value);
        } catch {
            // Measurement must never break typing.
        }
    }

    /** Rows for `text` were just put on screen. Only the first paint per keystroke counts. */
    painted(text: string, tier: SearchTier, rows: number): void {
        const session = this.session;
        const typed = this.typedAt.get(text);
        if (!session || !rows || typed === undefined) return;
        this.typedAt.delete(text);
        if (!session.sampled) return;
        afterPaint(() => {
            if (session.timings.length < MAX_TIMINGS) session.timings.push([lengthBucket(text), Math.round(now() - typed), tier]);
        });
    }

    /** The final answer for `text` (after any fallback) had `rows` rows. */
    answered(text: string, rows: number): void {
        const session = this.session;
        if (!session) return;
        session.answered += 1;
        if (rows) return;
        session.zeroCount += 1;
        if (session.zero.size < MAX_ZERO_QUERIES) session.zero.add(text.trim().toLowerCase().slice(0, 64));
    }

    /** The user opened the row at `rank` (0-based). */
    selected(rank: number): void {
        if (this.session) this.session.selectedRank = rank;
    }

    /** The box was closed (pick, blur, unmount): report the session, if sampled, and start over. */
    end(): void {
        const session = this.session;
        this.session = null;
        this.typedAt.clear();
        if (!session || !session.sampled || !session.queries) return;
        const source = this.source;
        // Read now: a pick by link changes the path on its own schedule.
        const navigated = typeof window !== "undefined" && window.location.pathname !== session.startPath;
        whenIdle(() => {
            try {
                trackSearchSession(source, {
                    sample_rate: SAMPLE_RATE,
                    queries: session.queries,
                    answered: session.answered,
                    zero_results: session.zeroCount,
                    zero_queries: Array.from(session.zero),
                    timings: session.timings,
                    selected_rank: session.selectedRank,
                    abandoned: session.selectedRank === null && !navigated,
                });
            } catch {
                // Analytics never surfaces an error.
            }
        });
    }
}
